/**
 * useSpatialDatasetRetry — re-run a failed spatial-dataset conversion.
 *
 * Why a hook and not a handler inside SpatialDatasetDetail:
 * POSTing to /process/ is only half the job. The drawer list poll
 * (SideBar's `refetchInterval`) runs only while some row is live
 * (`isLiveAsset`). A retry that only fires the POST leaves the cached row at
 * `failed`, so nothing refetches and the tile keeps saying "Failed" until the
 * page is reloaded.
 *
 * Writing the status the backend just returned into the cache does two things
 * at once: it flips the tile immediately, and — because the row is no longer in
 * a terminal state — it re-arms the existing poll, which then carries the row
 * through Queued → Processing → Ready on its own. No new polling code.
 *
 * Any surface that can retry (detail card today; a tile button or a bulk retry
 * later) should go through here so they all behave identically.
 */

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { processSpatialDataset } from "../services/spatialDatasetService";
import { applyAssetStatus } from "../services/assetStatusCache";
import { handleApiError } from "../utilities/errorHandlers";
import type { SpatialDataset, SpatialDatasetProcessingStatus } from "../types";

/** The minimum a caller needs to hand over — a full SpatialDataset satisfies it. */
export type RetryableDataset = Pick<SpatialDataset, "id" | "name" | "raster_output">;

/** Reads the server's own explanation out of an axios error, if it sent one. */
function apiDetail(error: unknown): string | null {
  const data = (error as any)?.response?.data;
  const detail = data?.detail ?? data?.error ?? data?.message;
  return typeof detail === "string" && detail.trim() ? detail.trim() : null;
}

export function useSpatialDatasetRetry() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Keyed by id rather than a single boolean so a list of tiles — or a future
  // bulk retry — can spin each row independently.
  const [retryingIds, setRetryingIds] = useState<ReadonlySet<number>>(() => new Set());

  const markRetrying = useCallback((id: number, active: boolean) => {
    setRetryingIds((prev) => {
      if (prev.has(id) === active) return prev;
      const next = new Set(prev);
      if (active) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const applyStatus = useCallback(
    (id: number, processing_status: SpatialDatasetProcessingStatus) => applyAssetStatus(queryClient, id, processing_status),
    [queryClient]
  );

  /**
   * Re-queue conversion for one dataset. Resolves `true` when the backend
   * accepted and actually triggered the job.
   */
  const retry = useCallback(
    async (dataset: RetryableDataset): Promise<boolean> => {
      const id = Number(dataset.id);
      markRetrying(id, true);
      try {
        const res = await processSpatialDataset(id, dataset.raster_output ?? undefined);
        // Trust the status the backend reports over assuming "queued" — if it
        // declined to re-run, the UI should say so rather than show a fake queue.
        await applyStatus(id, res?.processing_status ?? "queued");

        if (res?.processing_triggered === false) {
          enqueueSnackbar("The server did not start a new conversion for this dataset.", { variant: "warning" });
          return false;
        }
        enqueueSnackbar("Conversion re-queued", { variant: "success" });
        return true;
      } catch (error) {
        // Without this the button simply stopped spinning and said nothing.
        const detail = apiDetail(error);
        const handled = handleApiError(error, { action: "retry conversion for", itemType: dataset.name || "this dataset" });
        enqueueSnackbar(detail && handled.type === "generic_error" ? `Could not retry conversion: ${detail}` : handled.message, {
          variant: handled.variant
        });
        return false;
      } finally {
        markRetrying(id, false);
      }
    },
    [applyStatus, enqueueSnackbar, markRetrying]
  );

  const isRetrying = useCallback((id: number | string) => retryingIds.has(Number(id)), [retryingIds]);

  return { retry, isRetrying, retryingIds } as const;
}
